<template>
  <AccordionCardInfoDisplay
    v-if="showForEntityTypes.includes(clickedEntityType)"
    :heading="title"
    :isExpanded="isExpanded"
    @onClicked="handleClick"
  >
    <template #content>
      <div v-if="!isLoading && placeSummary.length" class="place">
        <div v-for="(item, index) in placeSummary" :key="index" class="place__item">
          <DividerHorizontal v-if="item.isHeader && item.hasDivider" />
          <div v-if="item.isHeader" class="place__item__section-header">
            {{ item.title }}
            <TooltipStandardV2
              v-if="item.info"
              class="place__item__info"
              :text="item.info"
              :arrowPosition="'bottom-center'"
              :vertical-offset="2"
            >
              <IconInfoCircle class="place__item__info" />
            </TooltipStandardV2>
          </div>
          <header v-if="!item.isHeader">
            {{ item.title }}
            <TooltipStandardV2
              v-if="item.info"
              class="place__item__info"
              :text="item.info"
              :arrowPosition="'bottom-center'"
              :vertical-offset="2"
            >
              <IconInfoCircle class="place__item__info" />
            </TooltipStandardV2>
          </header>
          <section v-if="!item.isHeader && item.value1?.length">
            {{ item.value1 }} {{ item.unit ?? '' }}
          </section>
          <section v-if="!item.isHeader && item.value2?.length">
            {{ item.value2 }}
          </section>
        </div>
      </div>
      <div v-else-if="isLoading" class="loading-spinner">
        <PulseLoader :loading="true" color="#3F46D6" />
      </div>
      <div v-else class="no-data">
        <p>{{ t('general__data__no-data-published') }}</p>
      </div>
    </template>
  </AccordionCardInfoDisplay>
</template>
<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useSelectionStore } from '@/pinia/selection'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { CoreEntityDto } from '@/interfaces/core.interface'
import {
  generateAddress,
  generateAddressList,
  generatePlaceSummary,
  generatePoiPlaceSummary,
  generateZoneSummary,
  placeSummaryItem
} from '@/utils/entityPlace'
import AccordionCardInfoDisplay from '@/components/features/info-display/AccordionCardInfoDisplay.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import IconInfoCircle from '@/design-system/assets/icons/IconInfoCircle.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TooltipStandardV2 from '@/design-system/atoms/TooltipStandardV2.vue'

const props = defineProps<{
  currentEntity: CoreEntityDto
  isLoading: boolean
  poiMetaInfo: any[]
  showForEntityTypes: string[]
  title: string
}>()

const selectionStore = useSelectionStore()
const { t } = useI18n()
const { clickedEntityType } = storeToRefs(selectionStore)

const currentEntity = computed(() => props.currentEntity)
const isExpanded = ref(false)

const placeSummary = computed(() => {
  let placeArray: placeSummaryItem[] = []
  // Address list first
  if (clickedEntityType.value === 'REAL_ESTATE') {
    placeArray = placeArray.concat(generateAddress(currentEntity.value))
  } else if (clickedEntityType.value === 'CONSTRUCTION') {
    placeArray = placeArray.concat(generateAddressList(currentEntity.value))
  }
  // Place ("city" or similar and coordinates) second
  if (clickedEntityType.value === 'COMPLEX' || clickedEntityType.value === 'REAL_ESTATE') {
    placeArray = placeArray.concat(generatePlaceSummary(currentEntity.value))
  }
  // POI specific only (heights and coordinates)
  if (clickedEntityType.value === 'POI' && poiMetaInfo.value?.length) {
    placeArray = placeArray.concat(generatePoiPlaceSummary(poiMetaInfo.value))
  }
  if (clickedEntityType.value === 'ZONE') {
    placeArray = placeArray.concat(generateZoneSummary())
  }
  return placeArray
})
const poiMetaInfo = computed(() => props.poiMetaInfo)

function handleClick() {
  isExpanded.value = !isExpanded.value
}
</script>
<style scoped lang="scss">
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.place {
  overflow: hidden;
  flex: 1 1 auto;
  height: fit-content;
  z-index: 1;
  width: 100%;
  padding-top: 4px;
  &__item {
    &:not(:last-child) {
      padding-bottom: 8px;
    }
    &__info {
      color: variables.$color-greyscale-secondary;
      display: inline-flex;
      height: 16px;
      min-width: 16px;
      max-width: 16px;
    }
    &__section-header {
      @include font-mixins.desktop-bold-paragraph;
      align-items: center;
      color: variables.$color-greyscale-content;
      display: flex;
      gap: 4px;
      padding-top: 8px;
    }
  }
}
.loading-spinner {
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.no-data {
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  p {
    @include font-mixins.desktop-regular-label;
  }
}
header {
  @include font-mixins.desktop-bold-label;
  color: variables.$color-greyscale-secondary;
  display: flex;
  gap: 4px;
}
section {
  @include font-mixins.desktop-regular-paragraph;
  color: variables.$color-greyscale-content;
}
</style>
